// client/src/v2/procurar.ts
// PROCURAR NO CADERNO — achar a palavra sem estragar o que está escrito.
//
// ── A REGRA QUE DECIDE O DESENHO ────────────────────────────────────────
//
// O texto vive dentro de um `contentEditable`, e o que está no DOM é lido de
// volta como documento a cada tecla. Então QUALQUER marcação que a busca
// enfiasse ali — um `<mark>` em volta do achado, por exemplo — viraria
// conteúdo da nota: procurar "casa" deixaria "casa" marca-texto para sempre.
//
// Por isso nada aqui toca no conteúdo. São dois caminhos, os dois sem escrever
// no DOM:
//
//   · a SELEÇÃO do navegador, que já sabe pintar um trecho e rolar até ele;
//   · a API de realce do CSS (`CSS.highlights`), que pinta uma lista de
//     trechos sem criar elemento nenhum. Onde ela não existir, só a seleção
//     aparece — a busca continua funcionando, pintando um achado por vez.
//
// ── SEM ACENTO, DOS DOIS LADOS ──────────────────────────────────────────
//
// Procurar "coracao" tem que achar "coração": ninguém digita acento numa
// caixa de busca com pressa. Tirar o acento muda o tamanho da string, e aí o
// achado apontaria para a letra errada — daí o mapa que devolve cada posição
// sem acento à posição original.

export type Achado = {
  folha: number;
  no: Text;
  ini: number;
  fim: number;
};

const NOME_REALCE = 'forjamente-achado';

/** Texto sem acento e sem caixa, com o mapa de volta às posições originais.
 *
 *  Cada caractere vira exatamente um: a decomposição gera a letra base mais os
 *  sinais, e os sinais são descartados. Sem esse cuidado, "coração" (8 letras)
 *  viraria uma string de tamanho diferente e o achado marcaria o pedaço errado. */
function aplanar(texto: string): { plano: string; mapa: number[] } {
  let plano = '';
  const mapa: number[] = [];
  for (let i = 0; i < texto.length; i++) {
    const limpo = texto[i].normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    if (!limpo) continue;         // o caractere ERA só um sinal
    plano += limpo[0];            // ligaduras (æ) contam como uma
    mapa.push(i);
  }
  return { plano, mapa };
}

/** Os nós de texto de um elemento, na ordem em que aparecem. */
function nosDeTexto(raiz: Node): Text[] {
  const saida: Text[] = [];
  const andar = (no: Node) => {
    if (no.nodeType === Node.TEXT_NODE) {
      if ((no.nodeValue || '').length) saida.push(no as Text);
      return;
    }
    for (const filho of Array.from(no.childNodes)) andar(filho);
  };
  andar(raiz);
  return saida;
}

/** Todas as ocorrências do termo, folha por folha, na ordem de leitura.
 *
 *  A busca é feita no texto CORRIDO de cada folha, e não nó por nó: "coração"
 *  pode estar partido em três nós se metade dele estiver em negrito, e uma
 *  busca por nó não acharia nada. */
export function procurar(folhas: (HTMLElement | null)[], termo: string): Achado[] {
  const alvo = aplanar(String(termo || '')).plano.trim();
  if (alvo.length < 2) return [];   // uma letra só acharia a folha inteira

  const achados: Achado[] = [];
  folhas.forEach((el, indice) => {
    if (!el) return;
    const nos = nosDeTexto(el);
    if (!nos.length) return;

    // O texto da folha inteira, e de onde cada pedaço veio.
    let corrido = '';
    const dono: { no: Text; base: number }[] = [];
    for (const no of nos) {
      dono.push({ no, base: corrido.length });
      corrido += no.nodeValue || '';
    }
    const { plano, mapa } = aplanar(corrido);

    let de = 0;
    for (;;) {
      const onde = plano.indexOf(alvo, de);
      if (onde < 0) break;
      de = onde + 1;

      const iniCorrido = mapa[onde];
      const fimCorrido = (mapa[onde + alvo.length - 1] ?? iniCorrido) + 1;

      // De volta ao nó: o achado pode começar num e terminar noutro. Marcamos
      // o pedaço que cabe no nó de início — é o suficiente para pintar e rolar,
      // e evita um intervalo que atravessa fronteiras de elemento.
      const casa = [...dono].reverse().find((d) => d.base <= iniCorrido);
      if (!casa) continue;
      const dentro = iniCorrido - casa.base;
      const tamanho = Math.min(fimCorrido - iniCorrido, (casa.no.nodeValue || '').length - dentro);
      if (tamanho <= 0) continue;
      achados.push({ folha: indice, no: casa.no, ini: dentro, fim: dentro + tamanho });
    }
  });
  return achados;
}

function intervalo(a: Achado): Range {
  const r = document.createRange();
  r.setStart(a.no, a.ini);
  r.setEnd(a.no, a.fim);
  return r;
}

/** Pinta TODOS os achados de uma vez, sem criar elemento nenhum.
 *
 *  Onde `CSS.highlights` não existir, não faz nada — e não faz falta: a
 *  seleção continua mostrando o achado da vez. */
export function realcar(achados: Achado[]) {
  const css: any = (window as any).CSS;
  const Realce: any = (window as any).Highlight;
  if (!css || !css.highlights || !Realce) return;
  try {
    css.highlights.delete(NOME_REALCE);
    if (!achados.length) return;
    css.highlights.set(NOME_REALCE, new Realce(...achados.map(intervalo)));
  } catch { /* navegador sem a API: a seleção basta */ }
}

export function limparRealce() {
  const css: any = (window as any).CSS;
  if (css && css.highlights) { try { css.highlights.delete(NOME_REALCE); } catch {} }
}

/** Leva a tela até o achado e o deixa selecionado. */
export function irPara(a: Achado) {
  const r = intervalo(a);
  const sel = window.getSelection();
  if (sel) { sel.removeAllRanges(); sel.addRange(r); }
  const alvo = a.no.parentElement;
  if (alvo) alvo.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
}

export const CLASSE_REALCE = NOME_REALCE;
